import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CSharpUtils from '../../felles/CSharpUtils';
import { AppState } from '../../store/rootReducer';
import * as actionsNV06 from '../../store/actions/indexNV06';
import { AlfaKodeOversiktItem } from '../../models/NV06AlfaOversikt';
import { fetchAlfaKodeOversikt } from '../../felles/APICalls_NV06';

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof AlfaKodeOversiktItem;
  label: string;
  numeric: boolean;
  sortField: string; // maps to backend SortBy parameter
}

const headCells: HeadCell[] = [
  { id: 'alpha', label: 'ALF', numeric: false, sortField: 'alfakode' },
  { id: 'Group', label: 'VGR', numeric: false, sortField: 'varegruppe' },
  { id: 'groupName', label: 'Navn Varegruppe', numeric: false, sortField: 'navnvaregruppe' },
  { id: 'stocklines', label: 'Ant. Varelinjer', numeric: true, sortField: 'antallvarelinjer' },
];

interface NV06AlfaOversiktProps {
  size: { width: number; height: number };
}

export default function NV06AlfaOversikt({ size }: NV06AlfaOversiktProps) {
  const dispatch = useDispatch();
  const companyId = Number(CSharpUtils.T_companyID) || 0;

  const data = useSelector((state: AppState) => state.NV06?.data);
  const isFetching = useSelector((state: AppState) => state.NV06?.isFetching);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof AlfaKodeOversiktItem>('alpha');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Debounce search input — waits 400ms after user stops typing
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0); // reset to first page on new search
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]);

  // Map frontend column id to backend SortBy value
  const sortField = headCells.find((h) => h.id === orderBy)?.sortField ?? 'alfakode';

  // Fetch data from server whenever search, sort, or pagination changes
  useEffect(() => {
    if (companyId > 0) {
      dispatch(
        actionsNV06.fetchAlfaOversiktRequest(
          companyId,
          page + 1, // backend is 1-based
          rowsPerPage,
          debouncedSearch || undefined,
          sortField,
          order,
        )
      );
    }
  }, [dispatch, companyId, page, rowsPerPage, debouncedSearch, sortField, order]);

  const items = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;

  const handleSort = (property: keyof AlfaKodeOversiktItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [isExporting, setIsExporting] = useState(false);

  // Export ALL matching rows (with current search/sort) to CSV
  const handleExport = async () => {
    if (totalElements === 0) return;
    setIsExporting(true);
    try {
      const response = await fetchAlfaKodeOversikt(
        companyId,
        1,
        totalElements, // fetch all matching rows
        debouncedSearch || undefined,
        sortField,
        order,
      );
      const allRows = response.data?.content ?? [];
      if (allRows.length === 0) return;
      const separator = ';';
      const headers = ['ALF', 'VGR', 'Navn Varegruppe', 'Ant. Varelinjer'];
      const rows = allRows.map((item) =>
        [item.alpha, item.Group, item.groupName, item.stocklines].join(separator)
      );
      const csvContent = '\uFEFF' + [headers.join(separator), ...rows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Alfakodeoversikt_${new Date().getTime()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Alfakodeoversikt
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tabellen viser alle registrerte alfa/varegrupper med antall opprettede artikler
          </Typography>
        </Box>
              <Tooltip title="Klikk på kolonneoverskriftene for å sortere" style={{ cursor: "pointer", color: "#1d6f42" }}>
                  <IconButton size="small">
                      
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search and Export */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Søk i tabellen... (f.eks. 8* for alle som starter på 8)"
          value={searchInput}
          onChange={handleSearchChange}
          sx={{ width: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={totalElements === 0 || isExporting}
          sx={{
            backgroundColor: '#1d6f42',
            '&:hover': { backgroundColor: '#165a35' },
          }}
        >
          {isExporting ? 'Eksporterer...' : 'Eksporter til Excel'}
        </Button>
      </Box>

      {/* Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Viser {items.length} av {totalElements} rader
      </Typography>

      {/* Table */}
      <TableContainer sx={{ maxHeight: size.height ? size.height - 330 : 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Laster data...
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Ingen data funnet
                </TableCell>
              </TableRow>
            ) : (
              items.map((row, index) => (
                <TableRow key={`${row.alpha}-${row.Group}-${index}`} hover>
                  <TableCell>{row.alpha}</TableCell>
                  <TableCell>{row.Group}</TableCell>
                  <TableCell>{row.groupName}</TableCell>
                  <TableCell align="right">{row.stocklines}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100, 250]}
        labelRowsPerPage="Rader per side:"
        labelDisplayedRows={({ from, to, count }) => `${from}\u2013${to} av ${count}`}
      />
    </Paper>
  );
}
